<template>
  <ion-page>
    <ion-content
      ref="contentRef"
      :scroll-y="true"
      class="ion-content-scroll-host welcome-content">
      <!-- Slide Transition -->
      <Transition name="fade" mode="out-in">
        <div :key="currentStep" class="relative h-full">
          <!-- Full-screen background image (only for info slides, not onboarding) -->
          <div class="fixed inset-0 z-0">
            <img
              v-if="currentSlideImage && currentStep < 5"
              :src="currentSlideImage"
              :alt="slides[currentStep].title"
              class="object-cover w-full h-full brightness-125 grayscale" />
            <!-- Fallback background -->
            <div v-else class="w-full h-full spectra-background-05"></div>
          </div>

          <!-- Spectra color overlay -->
          <div
            v-if="currentStep === 0"
            class="fixed inset-0 z-10 opacity-80 mix-blend-multiply"
            :style="{ backgroundImage: spectraGradient }"></div>
          <div
            v-else
            class="fixed inset-0 z-10 opacity-80 mix-blend-multiply"
            :style="{ backgroundColor: currentSlideColor }"></div>

          <!-- Dark gradient overlay for text readability -->
          <div
            class="fixed inset-0 z-20 bg-gradient-to-t to-transparent from-black/80 via-black/30"></div>

          <!-- Scrollable content area -->
          <div class="relative z-30 h-full">
            <div
              class="flex flex-col px-6 pb-44 min-h-full lg:px-12"
              :class="
                slides[currentStep].type === 'form'
                  ? 'pt-32'
                  : 'pt-[50vh] justify-end'
              ">
              <!-- Desktop: constrain width and center, Mobile: full width -->
              <div class="mx-auto max-w-4xl lg:max-w-6xl">
                <h3
                  class="mb-4 text-4xl font-bold leading-tight text-white lg:text-6xl xl:text-7xl text-balance">
                  {{ slides[currentStep].title }}
                </h3>
                <p
                  class="mb-6 max-w-3xl text-lg font-medium text-white/90 lg:text-xl xl:text-2xl">
                  {{ slides[currentStep].description }}
                </p>

                <!-- Onboarding form content with enhanced styling for dark backdrop -->
                <div
                  v-if="slides[currentStep].type === 'form'"
                  class="mx-auto mt-8 max-w-lg onboarding-form"
                  :style="{ '--slide-color': currentSlideColor }"
                  @focusin="handleInputFocus">
                  <div
                    v-if="slides[currentStep].key === 'username'"
                    class="space-y-4">
                    <UsernameSection
                      ref="usernameRef"
                      :profile="profile"
                      :auto-edit="true"
                      :hide-cancel="true"
                      :show-random-option="true" />
                    <DisplayNameSection
                      ref="displayNameRef"
                      :profile="profile"
                      :auto-edit="true"
                      :hide-cancel="true" />
                  </div>

                  <div
                    v-if="slides[currentStep].key === 'avatar'"
                    class="space-y-4 avatar-section-container">
                    <AvatarSection :profile="profile" :auto-edit="true" />
                  </div>

                  <div
                    v-if="slides[currentStep].key === 'follow'"
                    class="flex justify-center space-y-4">
                    <div class="w-full max-w-md">
                      <FollowSuggestionsSection :profile="profile" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Pinned navigation button -->
          <div
            class="fixed bottom-20 left-1/2 z-40 transform -translate-x-1/2 lg:bottom-12">
            <button
              v-if="currentStep < slides.length - 1"
              class="flex justify-center items-center px-8 py-4 text-lg font-semibold text-black bg-white rounded-full shadow-xl backdrop-blur-sm transition-all duration-200 cursor-pointer hover:bg-white/90"
              @click="nextStep">
              Next
              <fa-icon icon="arrow-right" class="ml-2" />
            </button>
            <button
              v-else
              class="flex justify-center items-center px-8 py-4 text-lg font-semibold text-black bg-white rounded-full shadow-xl backdrop-blur-sm transition-all duration-200 cursor-pointer hover:bg-white/90"
              @click="handleEnd">
              {{ currentStep >= 5 ? "Let's Go" : 'Get Started' }}
              <fa-icon icon="rocket" class="ml-2" />
            </button>
          </div>

          <!-- Back button (top-left) -->
          <button
            v-if="currentStep > startingStep"
            :class="[
              'fixed cursor-pointer left-6 z-40 flex items-center justify-center w-12 h-12 text-white bg-black/30 rounded-full hover:bg-black/50 transition-all duration-200 backdrop-blur-sm cursor-pointer lg:left-12',
              { 'top-15': isNativeApp, 'top-6 lg:top-12': !isNativeApp }
            ]"
            @click="previousStep">
            <fa-icon icon="arrow-left" />
          </button>

          <!-- Step indicator (top-right) -->
          <div
            :class="[
              'fixed right-6 z-40 flex items-center space-x-2 lg:right-12',
              { 'top-15': isNativeApp, 'top-6 lg:top-12': !isNativeApp }
            ]">
            <div
              v-for="(slide, index) in slides"
              :key="index"
              class="w-2 h-2 rounded-full transition-all duration-200"
              :class="index === currentStep ? 'bg-white' : 'bg-white/30'"></div>
          </div>
        </div>
      </Transition>
    </ion-content>
  </ion-page>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, inject } from 'vue'
// import { Keyboard } from '@capacitor/keyboard'
import { useStoreProfile } from '@/stores/storeProfile'
import { useStoreTMDB } from '@/stores/storeTMDB'
import { useReviewColors } from '@/use/useReviewColors'
import UsernameSection from '@/components/UserSettings/UsernameSection'
import DisplayNameSection from '@/components/UserSettings/DisplayNameSection'
import AvatarSection from '@/components/UserSettings/AvatarSection'
import FollowSuggestionsSection from '@/components/UserSettings/FollowSuggestionsSection'

const storeProfile = useStoreProfile()
const storeTMDB = useStoreTMDB()
const { scoreHsl } = useReviewColors()
const { isNativeApp } = inject('platform')
const currentStep = ref(0)
const emit = defineEmits(['complete'])
const trendingTitles = ref([])
const slideColors = ref([])

// Computed property for current user profile (needed for onboarding forms)
const profile = computed(() => {
  return storeProfile.getCurrentUserProfile
})

// Computed property for starting step based on what user needs
const startingStep = computed(() => {
  return !storeProfile.needsWelcome && storeProfile.needsOnboarding ? 5 : 0
})

// Computed property to determine if user started at onboarding
const startedAtOnboarding = computed(() => {
  return startingStep.value === 5
})

// Component refs for onboarding forms
const usernameRef = ref(null)
const displayNameRef = ref(null)
const contentRef = ref(null)

const slides = ref([
  {
    title: 'Welcome to Spectra',
    description:
      "Discover tomorrow's favourites through real recommendations, not algorithms."
  },
  {
    title: 'Real Recommendations',
    description:
      'Discover great films and shows through the people you follow—not a popularity feed.'
  },
  {
    title: 'Look Up Anything',
    description:
      'From blockbusters to deep cuts—get the info you need, including where to watch.'
  },
  {
    title: 'Add Insight, Not Just Stars',
    description:
      "Spectra's color-coded system helps you share what stood out—good or bad—without the guesswork."
  },
  {
    title: 'Create & Share Lists',
    description:
      'Build collections, follow lists from people you trust, and keep track of what matters.'
  },
  {
    title: 'Pick a Username',
    description: 'Choose a unique username for your account.',
    type: 'form',
    key: 'username'
  },
  {
    title: 'Set an Avatar',
    description: 'Add a profile picture to personalize your account.',
    type: 'form',
    key: 'avatar'
  },
  {
    title: 'Add to Your Circle',
    description: 'Follow friends and discover new voices to see their reviews.',
    type: 'form',
    key: 'follow'
  }
])

// Computed properties
const currentSlideImage = computed(() => {
  const title = trendingTitles.value[currentStep.value]
  return title?.backdrop_path
    ? `https://image.tmdb.org/t/p/w1280${title.backdrop_path}`
    : null
})

const currentSlideColor = computed(() => {
  return slideColors.value[currentStep.value] || scoreHsl(7) // Default to green
})

// Create gradient from highest to lowest score colors (9 to 0)
const spectraGradient = computed(() => {
  const gradientStops = [
    scoreHsl(9), // Purple (highest)
    scoreHsl(8), // Blue
    scoreHsl(7), // Green
    scoreHsl(6), // Orange
    scoreHsl(5), // Yellow
    scoreHsl(4), // Pink/Magenta
    scoreHsl(3), // Red (lowest)
    scoreHsl(0) // Deep red
  ]

  return `linear-gradient(to bottom, ${gradientStops.join(', ')})`
})

const nextStep = async () => {
  // Save form data before advancing from onboarding slides
  if (slides.value[currentStep.value].type === 'form') {
    try {
      if (slides.value[currentStep.value].key === 'username') {
        // Save username and display name if they have changes
        if (usernameRef.value?.hasChanges()) {
          await usernameRef.value.saveForOnboarding()
        }
        if (displayNameRef.value?.hasChanges()) {
          await displayNameRef.value.saveForOnboarding()
        }
      }
      // Add other form types here as needed (avatar, follow)
    } catch (error) {
      console.error('Failed to save form data:', error)
      return // Don't advance if save failed
    }
  }

  // Complete welcome after finishing all info slides (slide 4 is the last info slide)
  if (currentStep.value === 4 && storeProfile.needsWelcome) {
    storeProfile.setWelcomeComplete()
  }

  if (currentStep.value < slides.value.length - 1) {
    currentStep.value++
  }
}

const previousStep = () => {
  if (currentStep.value > startingStep.value) {
    currentStep.value--
  }
}

// Cycle through core Spectra colors: red, orange, green, blue, purple
function getSpectraColorCycle() {
  const spectraColors = [
    3, // Red
    6, // Orange
    7, // Green
    8, // Blue
    9, // Purple
    8, // Blue (avatar slide - more appropriate than yellow)
    4, // Pink/Magenta
    2 // Deep red
  ]

  return slides.value.map((_, index) => {
    return spectraColors[index % spectraColors.length]
  })
}

// Fetch trending titles for background images
async function fetchTrendingTitles() {
  try {
    const response = await storeTMDB.fetchTrendingTitles({
      mediaType: 'all',
      timeWindow: 'week',
      page: 1
    })

    const titles = (response?.results || []).filter(
      (item) => !item.adult && item.backdrop_path
    )

    // Take as many as we have slides and shuffle, ensuring we have enough
    const shuffled = titles.sort(() => 0.5 - Math.random())
    // Repeat titles if we don't have enough to cover all slides
    const repeatedTitles = []
    while (repeatedTitles.length < slides.value.length) {
      repeatedTitles.push(...shuffled)
    }
    trendingTitles.value = repeatedTitles.slice(0, slides.value.length)

    // Cycle through Spectra color spectrum
    const colorScores = getSpectraColorCycle()
    slideColors.value = colorScores.map((score) => scoreHsl(score))
  } catch (error) {
    console.error('Error fetching trending titles:', error)
    // Generate colors anyway for fallback
    const colorScores = getSpectraColorCycle()
    slideColors.value = colorScores.map((score) => scoreHsl(score))
  }
}

const handleEnd = () => {
  // Complete welcome if user still needs it (fallback in case they somehow got here without completing it)
  if (storeProfile.needsWelcome) {
    storeProfile.setWelcomeComplete()
  }
  // Always complete onboarding when reaching the end
  storeProfile.setOnboardingComplete()
  emit('complete')
}

// Keyboard handling for iOS
// let keyboardShowListener = null
// let keyboardHideListener = null

function setKeyboardHeight(px) {
  document.documentElement.style.setProperty('--kb-height', `${px}px`)
}

function handleInputFocus(e) {
  const el = e.target
  requestAnimationFrame(() => {
    el.scrollIntoView({ block: 'center', behavior: 'smooth' })
  })
}

// Initialize on mount
onMounted(async () => {
  fetchTrendingTitles()

  // If user has completed welcome but still needs onboarding, start at slide 5
  currentStep.value = startingStep.value

  // Set up keyboard listeners for iOS
  // if (isNativeApp) {
  //   keyboardShowListener = await Keyboard.addListener(
  //     'keyboardWillShow',
  //     (info) => {
  //       setKeyboardHeight(info?.keyboardHeight ?? 320)
  //     }
  //   )

  //   keyboardHideListener = await Keyboard.addListener(
  //     'keyboardWillHide',
  //     () => {
  //       setKeyboardHeight(0)
  //     }
  //   )
  // }
})

// Clean up on unmount
onUnmounted(() => {
  // if (keyboardShowListener) keyboardShowListener.remove()
  // if (keyboardHideListener) keyboardHideListener.remove()
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

/* Ensure ion-content scrolls properly on iOS */
.ion-content-scroll-host {
  --overflow-hidden: visible !important;
}

/* Allow content to scroll when keyboard appears */
.ion-content-scroll-host .scroll-content {
  overflow-y: auto !important;
  height: 100% !important;
}

/* Add keyboard-aware padding */
.ion-content-scroll-host {
  padding-bottom: var(--kb-height, 0px);
}

/* Override global ion-content background for welcome slides */
.welcome-content {
  --background: #111827 !important;
}
</style>

<style>
/* Global overrides for avatar section in onboarding */
.onboarding-form [class*='bg-indigo-6'] {
  background-color: var(--slide-color) !important;
}

.onboarding-form [class*='border-indigo-6'] {
  border-color: var(--slide-color) !important;
}

.onboarding-form [class*='text-indigo-6'] {
  color: var(--slide-color) !important;
}

/* Upload icon and gray text color overrides */
.onboarding-form .text-gray-400,
.onboarding-form fa-icon.text-gray-400,
.onboarding-form .fa-icon.text-gray-400,
.onboarding-form [class*='text-gray-4'] {
  color: var(--slide-color) !important;
  opacity: 0.4 !important;
}

/* Target the cloud upload icon specifically */
.onboarding-form .fa-cloud-arrow-up {
  color: var(--slide-color) !important;
  opacity: 0.4 !important;
}
/* Force styles with higher specificity for onboarding */
.onboarding-form label,
.onboarding-form .text-sm,
.onboarding-form .text-xs {
  color: white !important;
  font-weight: 600 !important;
}

.onboarding-form .text-gray-800\/70,
.onboarding-form .text-gray-800,
.onboarding-form .text-gray-700,
.onboarding-form .text-gray-600,
.onboarding-form .text-gray-500 {
  color: rgba(255, 255, 255, 0.9) !important;
}

.onboarding-form .hover\:underline,
.onboarding-form .cursor-pointer {
  color: rgba(255, 255, 255, 0.8) !important;
}

/* Avatar sizing overrides */
.onboarding-form .size-16 {
  width: 6rem !important;
  height: 6rem !important;
}

.onboarding-form .md\:size-32 {
  width: 8rem !important;
  height: 8rem !important;
}

@media (min-width: 768px) {
  .onboarding-form .size-16 {
    width: 8rem !important;
    height: 8rem !important;
  }

  .onboarding-form .md\:size-32 {
    width: 10rem !important;
    height: 10rem !important;
  }
}
/* Fade transition */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.3s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
}

.fade-slide-enter-to,
.fade-slide-leave-from {
  opacity: 1;
}

/* Enhanced styling for onboarding forms on dark backdrop */
.onboarding-form :deep(label) {
  color: white !important;
  font-weight: 600;
}

/* Target all gray text variations */
.onboarding-form :deep(.text-gray-800\/70),
.onboarding-form :deep(.text-gray-800),
.onboarding-form :deep(.text-gray-700),
.onboarding-form :deep(.text-gray-600),
.onboarding-form :deep(.text-gray-500) {
  color: rgba(255, 255, 255, 0.9) !important;
}

.onboarding-form :deep(.hover\:text-gray-800) {
  color: rgba(255, 255, 255, 0.8) !important;
}

.onboarding-form :deep(.hover\:underline) {
  color: rgba(255, 255, 255, 0.8) !important;
}

.onboarding-form :deep(.bg-white\/40) {
  background-color: rgba(255, 255, 255, 0.9) !important;
}

.onboarding-form :deep(.text-blue-500) {
  color: rgb(59, 130, 246) !important;
}

.onboarding-form :deep(.text-indigo-600) {
  color: var(--slide-color) !important;
}

/* Avatar section styling - use slide color with higher specificity */
.onboarding-form .bg-indigo-600,
.onboarding-form button.bg-indigo-600 {
  background-color: var(--slide-color) !important;
}

.onboarding-form .hover\:bg-indigo-500:hover,
.onboarding-form button.hover\:bg-indigo-500:hover {
  background-color: var(--slide-color) !important;
  opacity: 0.9;
}

.onboarding-form .border-indigo-600,
.onboarding-form div.border-indigo-600,
.onboarding-form button.border-b-2 {
  border-color: var(--slide-color) !important;
}

.onboarding-form .border-dashed.border-indigo-600 {
  border-color: var(--slide-color) !important;
}

.onboarding-form .focus\:ring-indigo-500:focus {
  --tw-ring-color: var(--slide-color) !important;
}

/* Fix follow button visibility in onboarding - use blue to purple gradient */
.onboarding-form .from-gray-200.to-gray-300 {
  background: linear-gradient(to right, #3b82f6, #8b5cf6) !important;
  color: white !important;
}

.onboarding-form .text-gray-700 {
  color: white !important;
}

.onboarding-form .hover\:from-blue-300:hover {
  background: linear-gradient(to right, #2563eb, #7c3aed) !important;
  transform: scale(1.05) !important;
}

/* Style unfollow buttons differently in onboarding - use orange/amber for distinction */
.onboarding-form .from-sky-500.to-indigo-500 {
  background: linear-gradient(to right, #f59e0b, #d97706) !important;
  color: white !important;
}

.onboarding-form .hover\:from-red-400:hover {
  background: linear-gradient(to right, #ef4444, #dc2626) !important;
  transform: scale(1.05) !important;
}

/* Fix text readability in PersonListCard for onboarding */
.onboarding-form .text-gray-900 {
  color: rgb(17, 24, 39) !important;
}

.onboarding-form .text-gray-600 {
  color: rgb(75, 85, 99) !important;
}

.onboarding-form .text-black\/70 {
  color: rgb(55, 65, 81) !important;
}

.onboarding-form .text-white {
  color: rgb(17, 24, 39) !important;
}

/* Avatar section labels - make them white for dark background */
.avatar-section-container .text-gray-600,
.avatar-section-container .text-xs.text-gray-600 {
  color: white !important;
}

.onboarding-form :deep(.text-black) {
  color: rgb(31, 41, 55) !important;
}

/* Make avatar preview moderately sized in onboarding - more specific targeting */
.avatar-section-container .size-16 {
  width: 4rem !important;
  height: 4rem !important;
}

.avatar-section-container .md\:size-32 {
  width: 5rem !important;
  height: 5rem !important;
}

.avatar-section-container .relative.min-w-0.size-16 {
  width: 4rem !important;
  height: 4rem !important;
}

@media (min-width: 768px) {
  .avatar-section-container .size-16,
  .avatar-section-container .relative.min-w-0.size-16 {
    width: 5rem !important;
    height: 5rem !important;
  }

  .avatar-section-container .md\:size-32 {
    width: 6rem !important;
    height: 6rem !important;
  }
}

/* Scale avatar letters appropriately for smaller avatar */
.avatar-section-container div[style] span,
.avatar-section-container div[style] span[style],
.avatar-section-container [style*='fontSize'] {
  font-size: 1.25rem !important;
}

@media (min-width: 768px) {
  .avatar-section-container div[style] span,
  .avatar-section-container div[style] span[style],
  .avatar-section-container [style*='fontSize'] {
    font-size: 1.5rem !important;
  }
}

.avatar-section-container
  .relative.flex.items-center.text-white.justify-center.font-semibold
  * {
  font-size: 1.25rem !important;
}

@media (min-width: 768px) {
  .avatar-section-container
    .relative.flex.items-center.text-white.justify-center.font-semibold
    * {
    font-size: 1.5rem !important;
  }
}
</style>
